import { Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { createEffectFactory } from '@core/ngrx-legacy';

import { TellentGroupsHttpClient } from './tellent-groups.http-client';
import { TellentGroupsActions } from './tellent-groups.store';

@Injectable({ providedIn: 'root' })
export class TellentGroupsEffects {
  public effects = createEffectFactory(TellentGroupsActions, this.actions$);

  public fetchGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentGroupsActions.fetchPaginatedCollection),
      mergeMap(({ payload }) =>
        this.http.fetchGroups(payload.params).pipe(
          map(({ data, meta }) =>
            TellentGroupsActions.fetchPaginatedCollectionSuccess({
              payload: {
                data: { rows: data.map(row => ({ ...row, id: row.guid })), meta },
                scope: payload.params.scope ?? 'main',
              },
            }),
          ),
          catchError(error =>
            of(TellentGroupsActions.fetchPaginatedCollectionFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public addNewGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentGroupsActions.addNew),
      mergeMap(({ payload }) =>
        this.http.addGroup(payload).pipe(
          map(group =>
            TellentGroupsActions.addNewSuccess({ payload: { data: { ...group, id: group.guid } } }),
          ),
          catchError(({ error }) => of(TellentGroupsActions.addNewFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public editGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentGroupsActions.editGroup),
      mergeMap(({ payload }) =>
        this.http.updateOne(payload).pipe(
          map(group =>
            TellentGroupsActions.editGroupSuccess({
              payload: { data: { ...group, id: group.guid } },
            }),
          ),
          catchError(({ error }) => of(TellentGroupsActions.editGroupFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public removeGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentGroupsActions.removeGroup),
      mergeMap(({ payload }) =>
        this.http.removeGroup(payload).pipe(
          map(() => TellentGroupsActions.removeGroupSuccess()),
          catchError(error => of(TellentGroupsActions.addNewFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public addMembersToGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentGroupsActions.addMembers),
      mergeMap(({ payload }) =>
        this.http.addMembers(payload).pipe(
          map(group =>
            TellentGroupsActions.addMembersSuccess({
              payload: { data: { ...group, id: group.guid } },
            }),
          ),
          catchError(error => of(TellentGroupsActions.addMembersFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public removeMembersFromGroup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentGroupsActions.removeMembers),
      mergeMap(({ payload }) =>
        this.http.removeMembers(payload).pipe(
          map(group =>
            TellentGroupsActions.removeMembersSuccess({
              payload: { data: { ...group, id: group.guid } },
            }),
          ),
          catchError(error => of(TellentGroupsActions.removeMembersFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public fetchOne$ = this.effects(TellentGroupsActions.fetchOne, ({ payload }) =>
    this.http.fetchOne(payload).pipe(map(group => ({ data: { ...group, id: group.guid } }))),
  );

  constructor(
    private actions$: Actions,
    private http: TellentGroupsHttpClient,
  ) {}
}
